import React, { useContext } from 'react';
import { Page, PageContextType } from '../types';
import { PageContext } from '../PageContext';
import { useAuth, playSound } from '../AuthContext';
import { useAdmin } from '../AdminContext';

interface BottomNavProps {
    currentPage: Page;
}

interface NavItemProps {
    page: Page;
    title: string;
    icon: string;
    isActive: boolean;
    onClick: () => void;
    notificationCount?: number;
}

const NavItem: React.FC<NavItemProps> = ({ page, title, icon, isActive, onClick, notificationCount }) => (
    <button
        id={`tour-nav-${title.toLowerCase()}`}
        className={`nav-item flex flex-col items-center justify-center relative w-full ${isActive ? 'text-[var(--accent-pink)]' : 'text-gray-400 hover:text-gray-200'}`}
        onClick={onClick}
    >
        <i className={`ph-duotone ph-${icon} text-[28px] mb-1.5`}></i>
        {(notificationCount ?? 0) > 0 && (
            <span className="absolute top-0 right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                {notificationCount}
            </span>
        )}
        <p className="text-[9px] font-bold uppercase tracking-tighter whitespace-nowrap overflow-hidden text-ellipsis w-full px-0.5">{title}</p>
    </button>
);


const BottomNav: React.FC<BottomNavProps> = ({ currentPage }) => {
    const pageContext = useContext(PageContext);
    const { userProfile } = useAuth();
    const { notifications } = useAdmin();
    const lastClickRef = React.useRef<{ page: Page; time: number }>({ page: Page.Landing, time: 0 });

    const unreadCount = notifications.filter(n => !n.read).length;
    
    const handleNav = (page: Page, title: string) => {
        const now = Date.now();
        const doubleTapWindow = 300; // 300ms for double tap

        if (page === currentPage) {
            // Check for double tap on the same page
            if (lastClickRef.current.page === page && (now - lastClickRef.current.time) < doubleTapWindow) {
                console.log(`Double tap detected on ${page}, scrolling to top`);
                pageContext?.scrollToTop(true);
            }
            lastClickRef.current = { page, time: now };
            return; 
        }

        lastClickRef.current = { page, time: now };

        if (userProfile?.uiNavigationSound) {
            try {
                playSound(userProfile.uiNavigationSound);
            } catch (e) {
                console.warn("Navigation sound failed:", e);
            }
        }
        
        pageContext?.switchPage(page, title);
    };

    const navItems = [
        { page: Page.Home, title: 'Home', icon: 'house' },
        { page: Page.Search, title: 'Search', icon: 'magnifying-glass' },
        { page: Page.Categories, title: 'Categories', icon: 'list-bullets' },
        { page: Page.Profile, title: 'Profile', icon: 'user-circle' },
        { page: Page.Groups, title: 'Groups', icon: 'users-three' },
        { page: Page.Notifications, title: 'Alerts', icon: 'bell' },
    ];
    
    return (
        <nav 
            className="w-full grid grid-cols-6 gap-0 py-3 px-1 shadow-inner" 
            style={{ 
                backgroundColor: 'var(--primary-dark-purple)',
                paddingBottom: '0.75rem'
            }}
        >
            {navItems.map(item => (
                <NavItem 
                    key={item.page}
                    page={item.page}
                    title={item.title}
                    icon={item.icon}
                    isActive={currentPage === item.page}
                    onClick={() => handleNav(item.page, item.title)}
                    notificationCount={item.page === Page.Notifications ? unreadCount : undefined}
                />
            ))}
        </nav>
    );
};

export default BottomNav;